import React from 'react';

function fmt(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function pct(a, b) {
  if (!b) return 0;
  return Math.min(100, Math.max(0, Math.round((a / b) * 100)));
}

export default function FinancialCard({ financials: f }) {
  const opRatio = pct(f.operating_expenses, f.operating_revenue);
  const repRatio = pct(f.replacement_expenses, f.replacement_revenue);
  const badDebtPct = f.operating_revenue ? ((f.bad_debt / f.operating_revenue) * 100).toFixed(1) : 0;
  const opBalanceOk = f.operating_fund_balance > 0;
  const repBalanceOk = f.replacement_fund_balance > 50_000;

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {/* Summary row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        <FinStat label="Operating Revenue" value={fmt(f.operating_revenue)} />
        <FinStat label="Operating Expenses" value={fmt(f.operating_expenses)} border />
        <FinStat label="Bad Debt" value={`${badDebtPct}%`} sub="of revenue"
          color={parseFloat(badDebtPct) > 3 ? 'var(--risk-high)' : 'var(--text-primary)'} border />
      </div>

      {/* Fund balances */}
      <div style={{ padding: '20px 24px' }}>
        <div style={{ marginBottom: 20 }}>
          <FundBar
            label="Operating Fund Balance"
            value={f.operating_fund_balance}
            max={Math.max(Math.abs(f.operating_fund_balance), f.operating_revenue * 0.2)}
            positive={opBalanceOk}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <FundBar
            label="Replacement / Reserve Fund Balance"
            value={f.replacement_fund_balance}
            max={Math.max(f.replacement_fund_balance, f.replacement_revenue * 5)}
            positive={repBalanceOk}
          />
        </div>

        {/* Expense ratios */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
          <RatioBar label="Operating Expense Ratio" pct={opRatio} />
          <RatioBar label="Replacement Expense Ratio" pct={repRatio} />
        </div>
      </div>
    </div>
  );
}

function FinStat({ label, value, sub, color, border }) {
  return (
    <div style={{
      padding: '16px 20px',
      borderLeft: border ? '1px solid var(--border-subtle)' : 'none',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 600, color: color || 'var(--text-primary)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function FundBar({ label, value, max, positive }) {
  const color = positive ? 'var(--risk-low)' : 'var(--risk-high)';
  const fillPct = max > 0 ? Math.min(100, Math.abs(value) / max * 100) : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color }}>{positive ? '' : '-'}{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.abs(value))}</span>
      </div>
      <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 2,
          background: color,
          width: `${fillPct}%`,
          transition: 'width 0.8s cubic-bezier(0.34,1.56,0.64,1)',
          boxShadow: `0 0 8px ${color}`,
        }} />
      </div>
    </div>
  );
}

function RatioBar({ label, pct }) {
  const color = pct > 90 ? 'var(--risk-high)' : pct > 75 ? 'var(--risk-medium)' : 'var(--risk-low)';
  return (
    <div style={{
      background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '12px 14px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 600, color }}>{pct}%</span>
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>expenses / revenue</span>
      </div>
      <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2 }} />
      </div>
    </div>
  );
}
